import React from "react";
import { Card, Button, Form, Input, notification } from "antd";
import { Auth } from "aws-amplify";
import { CognitoUserAttribute } from "amazon-cognito-identity-js";
import { validateUserPassword } from "../../utils";
import "./styles.css";

interface ICustomSignUpState {
  loading: boolean;
  waitingVerificationCode: boolean;
  verificationCode: string;
  email: string;
  password: string;
  accountName: string;
  invitationCode: string;
}

interface ICustomSignUpProps {
  onAuthChange: (state: string, e: any) => void;
}

class CustomSignUp extends React.Component<ICustomSignUpProps, ICustomSignUpState> {
  state = {
    loading: false,
    waitingVerificationCode: false,
    verificationCode: "",
    email: "",
    password: "",
    accountName: "",
    invitationCode: ""
  };

  validate = () => {
    const { password, verificationCode, accountName } = this.state;
    let errors: string[] = validateUserPassword(password);
    if (verificationCode === "") errors.push("Verification code cannot be empty");
    if (accountName === "") errors.push("Account Name cannot be empty");

    if (errors.length > 0) {
      notification.error({
        message: "Password Validation Error",
        description: (
          <ul>
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        )
      });
      return false;
    }
    return true;
  };

  handleSubmit = async (e: any) => {
    e.preventDefault();
    if (!this.validate()) return;
    this.setState({ loading: true });
    try {
      await Auth.signUp({
        username: this.state.email.toLocaleLowerCase(),
        password: this.state.password,
        validationData: [
          new CognitoUserAttribute({
            Name: "accountName",
            Value: this.state.accountName
          }),
          new CognitoUserAttribute({
            Name: "invitationCode",
            Value: this.state.invitationCode
          })
        ]
      });
      this.setState({
        loading: false,
        waitingVerificationCode: true
      });
    } catch (e) {
      this.setState({ loading: false });
      if (e.code === "UsernameExistsException") {
        this.resendSignUp();
      } else if (e.message.match(/user.*confirmed/i)) {
        console.log("USER CONFIRMED");
        notification.warning({
          message: e.message
        });
        this.props.onAuthChange("signIn", {});
      } else if (e.message.match(/INVALID_INVITATION_CODE/i)) {
        notification.error({
          message: "INVALID INVITATION CODE",
          description: "The invitation code you provided is not valid"
        });
      } else {
        notification.error({
          message: "SignUp Error",
          description: e.message
        });
        console.log(e);
      }
    }
  };

  resendSignUp = async () => {
    try {
      await Auth.resendSignUp(this.state.email);
      this.setState({
        loading: false,
        waitingVerificationCode: true
      });
    } catch (e) {
      notification.error({
        message: e.code,
        description: e.message
      });
    }
  };

  handleConfirmSignUp = async (e: any) => {
    e.preventDefault();
    this.setState({ loading: true });
    try {
      await Auth.confirmSignUp(this.state.email, this.state.verificationCode);
      await Auth.signIn(this.state.email, this.state.password);
      this.props.onAuthChange("signedIn", {});
    } catch (e) {
      notification.error({
        message: "SignUp Error",
        description: e.message
      });
      this.setState({ loading: true });
      throw e;
    }
    this.setState({ loading: true });
  };

  handleResendSignUp = async (e: any) => {
    e.preventDefault();
    await Auth.resendSignUp(this.state.email);
  };

  renderSignUpForm() {
    const { email, password, invitationCode, accountName } = this.state;
    return (
      <Form layout="vertical">
        <Form.Item label="Email">
          <Input
            size="large"
            value={email}
            onChange={(e) => this.setState({ email: e.target.value })}
          />
        </Form.Item>
        <Form.Item label="Password">
          <Input
            size="large"
            type="password"
            value={password}
            onChange={(e) => this.setState({ password: e.target.value })}
          />
        </Form.Item>
        <Form.Item label="Account Name">
          <Input
            size="large"
            value={accountName}
            onChange={(e) => this.setState({ accountName: e.target.value })}
          />
        </Form.Item>
        <Form.Item label="Invitation Code">
          <Input
            size="large"
            value={invitationCode}
            onChange={(e) => this.setState({ invitationCode: e.target.value })}
          />
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            size="large"
            onClick={this.handleSubmit}
            style={{ width: "100%" }}
            loading={this.state.loading}
            disabled={this.state.loading}
          >
            Sign Up
          </Button>
        </Form.Item>

        <div>
          <Button type="link" size="large" onClick={() => this.props.onAuthChange("signIn", {})}>
            Back to Sign In
          </Button>
        </div>
      </Form>
    );
  }

  renderConfirmationForm = () => (
    <Form>
      <Form.Item label="Verification Code">
        <Input
          placeholder="Verification Code"
          value={this.state.verificationCode}
          size="large"
          onChange={(e) => this.setState({ verificationCode: e.target.value })}
        />
      </Form.Item>
      <Form.Item>
        <Button
          type="primary"
          className="signup-form-button"
          size="large"
          onClick={this.handleConfirmSignUp}
          loading={this.state.loading}
          disabled={this.state.loading}
          style={{ width: "100%" }}
        >
          Confirm Sign Up
        </Button>
      </Form.Item>

      <div style={{ textAlign: "right" }}>
        <Button type="link" size="large" onClick={this.handleResendSignUp}>
          Resend Confirmation Code
        </Button>
      </div>
    </Form>
  );

  render() {
    return (
      <div className="SignIn-Container">
        <Card
          className="SignIn-Card"
          title={
            <div style={{ textAlign: "center" }}>
              <img src="/logo-ketrics.png" alt="" width="300" />
            </div>
          }
        >
          {this.state.waitingVerificationCode
            ? this.renderConfirmationForm()
            : this.renderSignUpForm()}
        </Card>
      </div>
    );
  }
}

export default CustomSignUp;
